import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { SecureDataService } from '../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { LanguageStateService } from '../../common/services/language-state.service';

export interface MBADataModel {
  ambAgreementFileUrl?: string;
  smbMasterAgreementUrl?: string;
  showMasterAgreement?: boolean;
  smbMasterAgreementTermTitle?: string;
  smbMasterAgreementTermHtml?: string;
  smbAgreementTermTitle?: string;
  smbAgreementTermHtml?: string;
}

const MASTER_AGREEMENT_STATIC_URL = 'https://serviceguide.att.com/masteragreement/maDocViewExt.cfm?ID=252833';

@Component({
    selector: 'app-mobile-business-agreement',
    templateUrl: './mobile-business-agreement.component.html',
    styleUrl: './mobile-business-agreement.component.scss',
    standalone: false
})
export class MobileBusinessAgreementComponent extends BaseViewComponent {

  #secureDataService: SecureDataService = inject(SecureDataService)
  languageService: LanguageStateService = inject(LanguageStateService)
  fb: FormBuilder = inject(FormBuilder);

  pageForm: FormGroup | undefined;
  showCancelDialog = false;

  ambAgreementFileUrl = '';
  masterAgreementUrl = MASTER_AGREEMENT_STATIC_URL;
  showMasterAgreement = false;
  agreementTerms: MBADataModel = {};

  // CC supplies English agreement text only; other languages and missing values use translations.
  get masterAgreementTitle(): string {
    return this.getAgreementText(this.agreementTerms.smbMasterAgreementTermTitle, 'ATT_MASTER_AGREEMENT');
  }

  get masterAgreementTermHtml(): string {
    return this.getAgreementText(this.agreementTerms.smbMasterAgreementTermHtml, 'MASTER_AGREEMENT_AGREE_TERMS');
  }

  get ambAgreementTitle(): string {
    return this.getAgreementText(this.agreementTerms.smbAgreementTermTitle, 'ATT_MOBILE_BUSINESS_PRICING_SCHEDULE');
  }

  get ambAgreementTermHtml(): string {
    return this.getAgreementText(this.agreementTerms.smbAgreementTermHtml, 'MBA_PRICING_SCHEDULE_AGREE_TERMS');
  }

  protected override refreshView(): void {
    this.pageForm = this.fb.group({
      ambAccepted: [false, Validators.requiredTrue],
      signature: ['', Validators.required]
    });
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'MBAInfo' && val.Data != undefined) {
      const rawData = val.Data as MBADataModel;
      this.ambAgreementFileUrl = rawData.ambAgreementFileUrl;
      this.showMasterAgreement = rawData.showMasterAgreement;
      this.agreementTerms = rawData;

      if (this.showMasterAgreement) {
          this.masterAgreementUrl = rawData.smbMasterAgreementUrl ? rawData.smbMasterAgreementUrl : 'https://serviceguide.att.com/masteragreement/maDocViewExt.cfm?ID=252833';
        if (!this.pageForm?.contains('masterAccepted')) {
          this.pageForm?.addControl('masterAccepted', new FormControl(false, Validators.requiredTrue));
        }
      }
    }
  }

  private getAgreementText(ccValue: string | undefined, translationKey: string): string {
    if (this.languageService.getCurrentLanguage() === 'en' && ccValue?.trim()) {
      return ccValue;
    }
    return this.languageService.getTranslation(translationKey);
  }

  onAgreementLinkClick(url: string, controlName: string) {
    window.open(url, '_blank');
    setTimeout(() => {
      const control = this.pageForm?.get(controlName);
      if (control) {
        control.setValue(true);
        control.markAsTouched();
      }
    }, 0);
    return false;
  }

  onAgreementChecked(controlName: string, event: { checked?: boolean }) {
    if (event.checked) {
      this.notificationService.showWarning('Please read the terms by clicking the link before accepting.');
      this.pageForm?.get(controlName)?.setValue(false);
    }
  }

  onFormSubmit() {
    if (this.pageForm?.valid) {
      const data = {
        form: this.pageForm.value,
        isSubmitted: true
      };
      this.#secureDataService.submitCustomerData(data, 'MBAInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.pageForm?.markAllAsTouched();
      this.scrollToInvalidControl();
    }
  }

  onFormCancel() {
    this.showCancelDialog = true;
  }

  onCancelConfirmation(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('MBAInfo').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateWaiting(true);
          },
          error: (err) => this.onCancelError('MBAInfo', err)
        })
    }
  }
}
